import React, { Component } from 'react';
import SidePanel from './sidePanel/sidePanel';
import { IMainSidePanelProps } from './mainSidePanel.d';
import { ScrollLock } from '../../../Helper/scrollLock';
import { ContentType } from './sidePanel/contentType';
import { IEsAppService } from './esAppService';
import classnames from 'classnames';
import styles from './mainSidePanel.scss';
import { publish, subscribe, unsubscribe } from '../../Common/customEventHelper';
import {updatePageUrl} from '../../Common/html-helper'; 

export default class MainSidePanel extends Component<IMainSidePanelProps,
    { showSidebar: boolean, currentTab: ContentType }> {
    private scrollLock: ScrollLock;
    private esAppService: IEsAppService = new IEsAppService();
    private readonly shellOpenSignature = 'ESPP.MainSidePanel.ShouldOpen';
    private readonly shellOpenListener = (payload) => {
        this.open(payload);
    };
    private readonly openListener = (payload: CustomEvent) => {
        this.open(payload.detail);
    };
    private readonly closeListener = () => {
        this.close();
    };

    constructor(props) {
        super(props);
        this.state = { showSidebar: false, currentTab: ContentType.FilterContent };
        this.scrollLock = ScrollLock.instance;
        this.onCloseRequestedBySubComponent = this.onCloseRequestedBySubComponent.bind(this);
        this.onResetRequestedBySubComponent = this.onResetRequestedBySubComponent.bind(this);
        this.handleContentChange = this.handleContentChange.bind(this);
    }

    public render() {
        return (
            <div data-testid={'side-panel-app'} className={styles.side_panel_app}>
                <div data-testid={'sp-content'} className={classnames(styles.content,
                    { [styles.msp_hidden]: !this.state.showSidebar })}>
                    <SidePanel onClose={this.onCloseRequestedBySubComponent} onReset={this.onResetRequestedBySubComponent}
                        filterContentViewModel={this.props.filterContentViewModel}
                        currentTab={this.state.currentTab} onTabChange={this.handleContentChange}
                        isShow={this.state.showSidebar} shouldShowProductFinder={this.props.shouldShowProductFinder}
                        productFinderServicesUpAndRunning={this.props.productFinderServicesUpAndRunning}
                        globalState={this.props.globalState}
                        l10n={this.props.l10n}
                        productFinderKey={this.props.productFinderKey}
                    />
                </div>
            </div>
        );
    }

    public componentDidMount() {
        this.bindEvent();

        if (typeof window !== 'undefined' && window.shell) {
            if (new URL(window.location.href).searchParams.get('trouserfinder') === 'open') {
                updatePageUrl('trouserfinder');
                setTimeout(() => { this.open(ContentType.TrouserFinderContent); }, 300); // give some time for PF to react
            }

            if (new URL(window.location.href).searchParams.get('jacketfinder') === 'open') {
                updatePageUrl('jacketfinder');
                setTimeout(() => { this.open(ContentType.JacketFinderContent); }, 300); // give some time for PF to react
            }

            if (new URL(window.location.href).searchParams.get('shoefinder') === 'open') {
                updatePageUrl('shoefinder');
                setTimeout(() => { this.open(ContentType.ShoeFinderContent); }, 300); // give some time for PF to react
            }
        }
    }

    public componentWillUnmount() {
        unsubscribe('ESPP.MainSidePanel.ShouldOpen', this.openListener);
        unsubscribe('ESPP.MainSidePanel.ShouldClose', this.closeListener);

        if (typeof window !== 'undefined' && typeof window.shell !== 'undefined') {
            window.shell.unsubscribeFrom('ESPP.MainSidePanel.ShouldOpen', this.shellOpenSignature);
        }
    }

    public handleContentChange(tab: ContentType) {
        let payload: string = null;

        switch (this.state.currentTab) {
            case ContentType.FilterContent: {
                payload = ContentType.FilterContent;
                break;
            }
            case ContentType.TrouserFinderContent: {
                payload = ContentType.TrouserFinderContent;
                break;
            }
            case ContentType.JacketFinderContent: {
                payload = ContentType.JacketFinderContent;
                break;
            }
            case ContentType.ShoeFinderContent: {
                payload = ContentType.ShoeFinderContent;
                break;
            }
        }

        publish('ESPP.MainSidePanel.TabSwitchOff', payload);

        this.setState({ currentTab: tab }, this.finishOpening.bind(this, tab));
    }

    public open(payload: ContentType) {
        switch (payload) {
            case ContentType.TrouserFinderContent: {
                this.setState({ showSidebar: true, currentTab: ContentType.TrouserFinderContent },
                    this.finishOpening.bind(this, payload));
                break;
            }
            case ContentType.JacketFinderContent: {
                this.setState({ showSidebar: true, currentTab: ContentType.JacketFinderContent },
                    this.finishOpening.bind(this, payload));
                break;
            }
            case ContentType.ShoeFinderContent: {
                this.setState({ showSidebar: true, currentTab: ContentType.ShoeFinderContent },
                    this.finishOpening.bind(this, payload));
                break;
            }
            case ContentType.SortContent: {
                this.setState({ showSidebar: true, currentTab: ContentType.SortContent },
                    this.finishOpening.bind(this, payload));
                break;
            }
            default: {
                this.setState({ showSidebar: true, currentTab: ContentType.FilterContent },
                    this.finishOpening.bind(this, payload));
                break;
            }
        }

        if(window.shell.tabNav) {
            if (document.getElementsByClassName('fas_msp_close_button').length > 0) {
                const mspCloseBtn = document.querySelector('.fas_msp_close_button')
                window.shell.tabNav.focus(mspCloseBtn);
            }
        }
    }

    private onCloseRequestedBySubComponent() {
        if (document.getElementsByClassName('fas_tooltip_overlay').length > 0)
            document.getElementsByClassName('fas_tooltip_overlay')[0].classList.add('hide');

        this.close();
    }

    private onResetRequestedBySubComponent() {
        this.reset();
    }

    private bindEvent() {
        if (typeof window !== 'undefined' && typeof window.shell !== 'undefined') {
            window.esApp = this.esAppService;

            // for breadcrumb, because it is not in ATS scope
            window.shell.subscribeTo('ESPP.MainSidePanel.ShouldOpen', this.shellOpenListener, this.shellOpenSignature);
        }

        subscribe('ESPP.MainSidePanel.ShouldOpen', this.openListener);
        
        subscribe('ESPP.MainSidePanel.ShouldClose', this.closeListener);
    }

    private close() {
        if (typeof window !== 'undefined' && typeof window.shell !== 'undefined')
            window.shell.publishTo('ESPP.ProductFinder.LogCancelStep', this.state.currentTab);

        const sidebar = document.getElementsByClassName(styles.side_panel_app)[0];
        sidebar.classList.add(styles.slideout);
        this.esAppService.navigationWheel.show();
        this.scrollLock.unlock();
        setTimeout(() => {
            sidebar.classList.remove(styles.slideout);
            this.setState({ showSidebar: false }, () => {
                publish('ESPP.MainSidePanel.Closed', null);
            });
        }, 300);
    }

    private reset() {
        if (typeof window !== 'undefined' && typeof window.shell !== 'undefined')
            window.shell.publishTo('ProductFinder.Reset', this.props.productFinderKey);
    }

    private finishOpening(payload: ContentType) {
        if (typeof window !== 'undefined' && typeof window.shell !== 'undefined') {
            if (payload !== ContentType.SortContent && payload !== ContentType.FilterContent) {
                // ping finder to prepare for render:
                window.shell.publishTo('ProductFinder.Open', payload);
            }

            publish('ESPP.MainSidePanel.Opened', null);

            this.esAppService.navigationWheel.hide();
            if (!this.scrollLock.getLockState)
                this.scrollLock.lock();
        }
    }
}
